import type { ReadingSlide } from "../ui/StartReader";
import { THE_1700_READING } from "./the-1700-release.reading";
import { SPOT_PHISHING_READING } from "./spot-phishing.reading";
import { USING_AI_SAFELY_READING } from "./using-ai-safely.reading";
import { RANSOMWARE_OFFICE_READING } from "./ransomware-office.reading";
import { PASSWORDS_MFA_READING } from "./passwords-mfa.reading";
import { SOCIAL_ENGINEERING_READING } from "./social-engineering.reading";
import { MFA_FATIGUE_READING } from "./mfa-fatigue.reading";
import { INVOICE_THAT_CHANGED_READING } from "./invoice-that-changed.reading";
import { DATA_HANDLING_READING } from "./data-handling.reading";
import { PHISHING_OFFICE_READING } from "./phishing-office.reading";
import { SPEAR_PHISHING_OSINT_READING } from "./spear-phishing-osint.reading";
import { CLICKFIX_READING } from "./clickfix.reading";
import { ONE_CLICK_TOO_MANY_READING } from "./one-click-too-many.reading";
import { MESSAGE_FROM_IT_READING } from "./message-from-it.reading";
import { DEEPFAKE_WHALING_READING } from "./deepfake-whaling.reading";
import { QUISHING_RESTAURANT_READING } from "./quishing-restaurant.reading";
import { PAUSE_AND_VERIFY_READING } from "./pause-and-verify.reading";
import { SEND_IT_SAFELY_READING } from "./send-it-safely.reading";
import { SHARING_FILES_READING } from "./sharing-files.reading";
import { HOLIDAY_CALL_READING } from "./holiday-call.reading";
import { INCIDENT_REPORTING_READING } from "./incident-reporting.reading";
import { INSIDE_THE_BANK_READING } from "./inside-the-bank.reading";
import { MALWARE_READING } from "./malware.reading";
import { OFFICE_UNDER_ATTACK_READING } from "./office-under-attack.reading";
import { PC_TO_DARKWEB_READING } from "./pc-to-darkweb.reading";
import { REMOTE_WORK_BYOD_READING } from "./remote-work-byod.reading";
import { REMOTE_WORK_MAX_READING } from "./remote-work-max.reading";
import { WORKPLACE_POLICIES_READING } from "./workplace-policies.reading";

/**
 * "Read introduction" content per course. A course listed here shows the flat
 * course-overview start screen (StartHost) with an optional reading pane the
 * learner can read while the 3D room downloads; a course NOT listed keeps the
 * original cinematic intro card. Each entry owns its clean title, kicker, read
 * estimate and 1-3 reading slides (each slide carries its own animated SVG).
 */
export interface CourseReading {
  title: string; // clean course title shown on the start screen
  kicker?: string; // small label above the reading headline
  readMinutes: number; // "<n> min read"
  slides: ReadingSlide[]; // 1-3 slides
}

export const COURSE_READINGS: Record<string, CourseReading> = {
  "the-1700-release": {
    title: "The 17:00 Release",
    kicker: "Secure development \u00b7 AI coding assistants",
    readMinutes: 2,
    slides: THE_1700_READING,
  },
  "spot-phishing": {
    title: "Spot a phishing email",
    kicker: "Phishing \u00b7 spot the lure, check the link, report it",
    readMinutes: 2,
    slides: SPOT_PHISHING_READING,
  },
  "using-ai-safely": {
    title: "Using AI safely at work",
    kicker: "Everyday AI use \u00b7 data protection",
    readMinutes: 2,
    slides: USING_AI_SAFELY_READING,
  },
  "ransomware-office": {
    title: "Ransomware at the bank",
    kicker: "Ransomware \u00b7 verify the caller, refuse emailed updates",
    readMinutes: 2,
    slides: RANSOMWARE_OFFICE_READING,
  },
  "passwords-mfa": {
    title: "Passwords and sign-in",
    kicker: "Passwords \u00b7 unique logins and MFA",
    readMinutes: 2,
    slides: PASSWORDS_MFA_READING,
  },
  "social-engineering": {
    title: "The visitor",
    kicker: "Social engineering \u00b7 physical security",
    readMinutes: 2,
    slides: SOCIAL_ENGINEERING_READING,
  },
  "mfa-fatigue": {
    title: "MFA fatigue",
    kicker: "Identity \u00b7 sign-in requests on your phone",
    readMinutes: 2,
    slides: MFA_FATIGUE_READING,
  },
  "invoice-that-changed": {
    title: "The invoice that changed",
    kicker: "Payment fraud \u00b7 business email compromise",
    readMinutes: 2,
    slides: INVOICE_THAT_CHANGED_READING,
  },
  "data-handling": {
    title: "Handling data safely",
    kicker: "Data handling \u00b7 classify and share with care",
    readMinutes: 2,
    slides: DATA_HANDLING_READING,
  },
  "phishing-office": {
    title: "The bank that wasn\u2019t",
    kicker: "Phishing \u00b7 fake emails, logins and calls",
    readMinutes: 2,
    slides: PHISHING_OFFICE_READING,
  },
  "spear-phishing-osint": {
    title: "The perfect bait",
    kicker: "Spear phishing \u00b7 oversharing and targeted lures",
    readMinutes: 2,
    slides: SPEAR_PHISHING_OSINT_READING,
  },
  "clickfix": {
    title: "The fake browser fix",
    kicker: "Social engineering \u00b7 fake fixes you paste and run",
    readMinutes: 2,
    slides: CLICKFIX_READING,
  },
  "one-click-too-many": {
    title: "One click too many",
    kicker: "Shadow IT \u00b7 unapproved apps and permissions",
    readMinutes: 2,
    slides: ONE_CLICK_TOO_MANY_READING,
  },
  "message-from-it": {
    title: "The message from IT",
    kicker: "Phishing \u00b7 chat apps and app permissions",
    readMinutes: 2,
    slides: MESSAGE_FROM_IT_READING,
  },
  "deepfake-whaling": {
    title: "The CEO on the call",
    kicker: "Deepfakes \u00b7 a familiar face asking for money",
    readMinutes: 2,
    slides: DEEPFAKE_WHALING_READING,
  },
  "quishing-restaurant": {
    title: "The QR code at the table",
    kicker: "Quishing \u00b7 QR codes that hide where they lead",
    readMinutes: 2,
    slides: QUISHING_RESTAURANT_READING,
  },
  "pause-and-verify": {
    title: "Pause and verify",
    kicker: "Verification \u00b7 plausible is not proof",
    readMinutes: 2,
    slides: PAUSE_AND_VERIFY_READING,
  },
  "send-it-safely": {
    title: "Send it safely",
    kicker: "Data protection \u00b7 sharing files and access",
    readMinutes: 2,
    slides: SEND_IT_SAFELY_READING,
  },
  "sharing-files": {
    title: "Sharing files safely",
    kicker: "Collaboration \u00b7 the right file, the right people",
    readMinutes: 2,
    slides: SHARING_FILES_READING,
  },
  "holiday-call": {
    title: "The holiday call",
    kicker: "Deepfakes \u00b7 an urgent call and a face you can\u2019t trust",
    readMinutes: 2,
    slides: HOLIDAY_CALL_READING,
  },
  "incident-reporting": {
    title: "Report an incident",
    kicker: "Incident reporting \u00b7 what to report, and how",
    readMinutes: 2,
    slides: INCIDENT_REPORTING_READING,
  },
  "inside-the-bank": {
    title: "One password away",
    kicker: "Credentials \u00b7 reused passwords and public clues",
    readMinutes: 2,
    slides: INSIDE_THE_BANK_READING,
  },
  "malware": {
    title: "The game that wanted to run",
    kicker: "Malware \u00b7 one small yes after another",
    readMinutes: 2,
    slides: MALWARE_READING,
  },
  "office-under-attack": {
    title: "The office under attack",
    kicker: "One day \u00b7 six attacks \u00b7 you are the control",
    readMinutes: 2,
    slides: OFFICE_UNDER_ATTACK_READING,
  },
  "pc-to-darkweb": {
    title: "From your PC to the dark web",
    kicker: "Credentials \u00b7 how a work password gets stolen and sold",
    readMinutes: 2,
    slides: PC_TO_DARKWEB_READING,
  },
  "remote-work-byod": {
    title: "Remote work and your own devices",
    kicker: "Remote work \u00b7 the rules travel with your desk",
    readMinutes: 2,
    slides: REMOTE_WORK_BYOD_READING,
  },
  "remote-work-max": {
    title: "Working anywhere, safely",
    kicker: "Remote work \u00b7 home, travel and public spaces",
    readMinutes: 2,
    slides: REMOTE_WORK_MAX_READING,
  },
  "workplace-policies": {
    title: "Your workplace policies",
    kicker: "Company policies \u00b7 read, acknowledge, apply",
    readMinutes: 2,
    slides: WORKPLACE_POLICIES_READING,
  },
};
